import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Input,
  Button,
  Text,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useLogin } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loginMutation = useLogin({
    onSuccess: (data) => {
      // Use auth context to login
      login(data.user, {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        idToken: data.idToken,
      });
      
      toast({
        title: 'Login Successful',
        description: `Welcome back, ${data.user.firstName}!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Add a small delay to ensure auth context is updated
      setTimeout(() => {
        navigate('/profile');
      }, 100);
    },
    onError: (error: any) => {
      toast({
        title: 'Login Failed',
        description: error.message || 'Invalid email or password',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      loginMutation.mutate({
        email: formData.email,
        password: formData.password,
      });
    }
  };

  return (
    <Box py={16}>
      <Container maxW="md">
        <Box textAlign="center" mb={8}>
          <Heading size="xl" mb={4}>Welcome Back</Heading>
          <Text color="gray.600">
            Sign in to your ReDeuce account
          </Text>
        </Box>

        <Box bg="white" p={8} borderRadius="lg" shadow="md">
          <form onSubmit={handleSubmit}>
            <FormControl isInvalid={!!errors.email} mb={4}>
              <FormLabel fontWeight="semibold">Email Address</FormLabel>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                size="lg"
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.password} mb={6}>
              <FormLabel fontWeight="semibold">Password</FormLabel>
              <Input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                size="lg"
              />
              <FormErrorMessage>{errors.password}</FormErrorMessage>
            </FormControl>

            <Button
              type="submit"
              size="lg"
              width="full"
              bg="brand.50"
              color="black"
              _hover={{ bg: 'brand.100' }}
              mb={4}
              isLoading={loginMutation.isPending}
              loadingText="Signing In..."
            >
              Sign In
            </Button>

            <Text textAlign="center">
              Don't have an account?{' '}
              <Button variant="ghost" color="brand.200" onClick={() => navigate('/signup')}>
                Create one here
              </Button>
            </Text>
          </form>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage; 